/**
 * Bulk import: a CSV in, rows to review, drafts to save.
 *
 * Pure, so the node suite tests all of it and the browser does the work without
 * another route: the screen resolves locations and categories and then writes
 * each row through the same routes capture uses, which is what gives an import
 * the same validation, quota and refusals as typing the item in.
 *
 * A row is kept as the strings the person sees and edits. Nothing is parsed
 * into a number until `checkRow()` asks whether it could be, and `buildDraft()`
 * is the only place a row becomes an item.
 */

import { csvCell } from "./stock.js";
import { SERIAL_FIELD_LABEL } from "./stock.js";
import {
  CONDITION_LABELS,
  ITEM_CONDITIONS,
  ITEM_STATUSES,
  MAX_FIELDS_PER_ITEM,
  MAX_FIELD_LABEL_LENGTH,
  MAX_FIELD_VALUE_LENGTH,
  MAX_QUANTITY,
  MAX_TAGS_PER_ITEM,
  STATUS_LABELS,
  type FieldInput,
  type ItemCondition,
  type ItemDraft,
  type ItemStatus,
} from "./types.js";
import {
  MAX_NAME_LENGTH,
  MAX_TEXT_LENGTH,
  MAX_TITLE_LENGTH,
  centsFromInput,
  clampName,
  clampText,
  clampTitle,
  normaliseTags,
} from "./validate.js";

/** A typo bound, like the quota's: one import is a sitting's work, not a database load. */
export const MAX_IMPORT_ROWS = 500;
export const MAX_IMPORT_BYTES = 2 * 1024 * 1024;

/* -------------------------------------------------------------------- CSV -- */

/**
 * RFC 4180 with the three liberties spreadsheets take: a byte-order mark, a
 * semicolon or tab for a delimiter (chosen from the first line), and blank
 * lines. A quoted cell may hold the delimiter, a quote (doubled) or a newline.
 */
export function parseCsv(input: string): string[][] {
  const text = input.charCodeAt(0) === 0xfeff ? input.slice(1) : input;
  const firstLine = text.split(/\r\n|\n|\r/, 1)[0] ?? "";
  const count = (char: string) => {
    let inQuotes = false;
    let total = 0;
    for (const c of firstLine) {
      if (c === '"') inQuotes = !inQuotes;
      else if (c === char && !inQuotes) total++;
    }
    return total;
  };
  const delimiter = ([",", ";", "\t"] as const).reduce((best, next) =>
    count(next) > count(best) ? next : best,
  );

  const rows: string[][] = [];
  let row: string[] = [];
  let cell = "";
  let inQuotes = false;
  let touched = false;

  const endCell = () => {
    row.push(cell);
    cell = "";
  };
  const endRow = () => {
    endCell();
    if (row.some((value) => value.trim() !== "")) rows.push(row);
    row = [];
    touched = false;
  };

  for (let i = 0; i < text.length; i++) {
    const c = text[i] as string;
    if (inQuotes) {
      if (c === '"') {
        if (text[i + 1] === '"') {
          cell += '"';
          i++;
        } else inQuotes = false;
      } else cell += c;
      continue;
    }
    if (c === '"' && cell === "") {
      inQuotes = true;
      touched = true;
    } else if (c === delimiter) {
      endCell();
      touched = true;
    } else if (c === "\r" || c === "\n") {
      if (c === "\r" && text[i + 1] === "\n") i++;
      endRow();
    } else {
      cell += c;
      touched = true;
    }
  }
  if (touched || cell !== "") endRow();
  return rows;
}

/* ---------------------------------------------------------------- columns -- */

export type ImportColumn =
  | "title"
  | "description"
  | "category"
  | "location"
  | "tags"
  | "quantity"
  | "status"
  | "condition"
  | "price"
  | "notes"
  | "serial"
  | "cost"
  | "client"
  | "deployedOn";

export interface ImportColumnSpec {
  key: ImportColumn;
  /** The header the template writes, and what the mapping screen calls it. */
  label: string;
  hint: string;
  /** Normalised header names (lowercase, letters and digits only) that mean this column. */
  synonyms: readonly string[];
  /** Only meaningful in a stock inventory. */
  stockOnly?: boolean;
}

export const IMPORT_COLUMNS: readonly ImportColumnSpec[] = [
  {
    key: "title",
    label: "Title",
    hint: "What it is. Needed unless there is a serial number.",
    synonyms: [
      "title",
      "name",
      "itemname",
      "item",
      "displayname",
      "devicename",
      "systemname",
      "hostname",
      "assetname",
      "product",
      "productname",
    ],
  },
  {
    key: "description",
    label: "Description",
    hint: "Public: appears in listings.",
    synonyms: ["description", "details", "summary"],
  },
  {
    key: "category",
    label: "Category",
    hint: "One per item. Made if it is new.",
    synonyms: ["category", "type", "devicetype", "assettype", "nodeclass", "class", "role"],
  },
  {
    key: "location",
    label: "Location",
    hint: "A path, made if it is new: Basement > Shelf A > Bin 3",
    synonyms: ["location", "site", "room", "storage", "storagelocation", "place"],
  },
  {
    key: "tags",
    label: "Tags",
    hint: "Separated by ; or ,",
    synonyms: ["tags", "tag", "labels", "keywords"],
  },
  {
    key: "quantity",
    label: "Quantity",
    hint: "A whole number. 1 when blank.",
    synonyms: ["quantity", "qty", "count", "onhand"],
  },
  {
    key: "status",
    label: "Status",
    hint: "keep, for sale, sold or discarded. Keep when blank.",
    synonyms: ["status"],
  },
  {
    key: "condition",
    label: "Condition",
    hint: "new, like new, good, fair, poor or for parts.",
    synonyms: ["condition", "grade"],
  },
  {
    key: "price",
    label: "Price",
    hint: "What you would sell it for, e.g. 12.50",
    synonyms: ["price", "sellingprice", "listprice", "saleprice"],
  },
  {
    key: "notes",
    label: "Notes",
    hint: "Private: never in a listing or a prompt.",
    synonyms: ["notes", "note", "comments", "comment"],
  },
  {
    key: "serial",
    label: "Serial number",
    hint: "One unit per serial, so quantity must be 1.",
    synonyms: ["serial", "serialnumber", "serialno", "sn", "servicetag"],
  },
  {
    key: "cost",
    label: "Cost",
    hint: "What one unit cost you. Private.",
    synonyms: ["cost", "unitcost", "purchaseprice", "buyprice", "costprice"],
    stockOnly: true,
  },
  {
    key: "client",
    label: "Client",
    hint: "Already out at this client: deployed to them once it is on the shelf. Made if it is new.",
    synonyms: ["client", "clientname", "customer", "customername", "assignedto", "deployedto", "company"],
    stockOnly: true,
  },
  {
    key: "deployedOn",
    label: "Deployed on",
    hint: "When it went out to the client, as YYYY-MM-DD. Blank when not known: the import's date is used.",
    synonyms: ["deployedon", "deployeddate", "datedeployed", "deploymentdate", "assigneddate", "issuedon"],
    stockOnly: true,
  },
];

const columnSpec = (key: ImportColumn): ImportColumnSpec =>
  IMPORT_COLUMNS.find((column) => column.key === key)!;

export const normaliseHeader = (header: string): string => header.toLowerCase().replace(/[^a-z0-9]/g, "");

/** Where one source column goes. `field` and `private` make a custom field named after the header. */
export type ImportTarget = ImportColumn | "field" | "private" | "skip";

export interface ImportMapping {
  target: ImportTarget;
  /** For `field` and `private`: the field's label. */
  label: string;
}

const FIELD_PREFIX = /^(field|private)\s*:\s*(.*)$/i;

/**
 * A guess, never a decision: the person sees it and changes it. A header the
 * template wrote maps exactly; a header that is a well-known synonym maps to it;
 * `Field: X` and `Private: X` become custom fields; anything else is skipped
 * rather than turned into a field, since an export from a management tool has
 * dozens of columns nobody wants as fields.
 */
export function guessMapping(headers: readonly string[], stock: boolean): ImportMapping[] {
  const taken = new Set<ImportColumn>();
  return headers.map((header) => {
    const prefixed = FIELD_PREFIX.exec(header.trim());
    if (prefixed && (prefixed[2] as string).trim()) {
      return {
        target: (prefixed[1] as string).toLowerCase() === "private" ? "private" : "field",
        label: clampName(prefixed[2]).slice(0, MAX_FIELD_LABEL_LENGTH),
      };
    }
    const name = normaliseHeader(header);
    const match = IMPORT_COLUMNS.find(
      (column) => (!column.stockOnly || stock) && !taken.has(column.key) && column.synonyms.includes(name),
    );
    if (!match) return { target: "skip", label: "" };
    taken.add(match.key);
    return { target: match.key, label: "" };
  });
}

/* ------------------------------------------------------------------- rows -- */

export interface ImportRow {
  title: string;
  description: string;
  category: string;
  location: string;
  tags: string;
  quantity: string;
  status: string;
  condition: string;
  price: string;
  notes: string;
  serial: string;
  cost: string;
  client: string;
  deployedOn: string;
  /** Custom field values, in the order of `ImportSheet.fields`. */
  fields: string[];
}

export interface ImportSheet {
  fields: { label: string; visibility: "public" | "private" }[];
  rows: ImportRow[];
}

const blankRow = (fieldCount: number): ImportRow => ({
  title: "",
  description: "",
  category: "",
  location: "",
  tags: "",
  quantity: "",
  status: "",
  condition: "",
  price: "",
  notes: "",
  serial: "",
  cost: "",
  client: "",
  deployedOn: "",
  fields: new Array<string>(fieldCount).fill(""),
});

/** The table's data rows under a mapping. A row with nothing in any mapped cell is dropped. */
export function mapRows(
  table: readonly (readonly string[])[],
  mapping: readonly ImportMapping[],
): ImportSheet {
  const fields: ImportSheet["fields"] = [];
  const slot = new Map<number, number>();
  mapping.forEach((entry, index) => {
    if ((entry.target === "field" || entry.target === "private") && entry.label.trim()) {
      slot.set(index, fields.length);
      fields.push({
        label: entry.label.trim(),
        visibility: entry.target === "private" ? "private" : "public",
      });
    }
  });

  const rows: ImportRow[] = [];
  for (const cells of table.slice(1)) {
    const row = blankRow(fields.length);
    let any = false;
    mapping.forEach((entry, index) => {
      const value = (cells[index] ?? "").trim();
      if (!value || entry.target === "skip") return;
      any = true;
      const at = slot.get(index);
      if (at !== undefined) row.fields[at] = value;
      else if (entry.target !== "field" && entry.target !== "private") row[entry.target] = value;
    });
    if (any) rows.push(row);
  }
  return { fields, rows };
}

/* ----------------------------------------------------------------- parsing -- */

const bare = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[\s_-]+/g, " ");

export function parseStatus(value: string): ItemStatus | null {
  const word = bare(value);
  if (!word) return "keep";
  return (
    ITEM_STATUSES.find(
      (status) => status.replace("_", " ") === word || bare(STATUS_LABELS[status]) === word,
    ) ?? null
  );
}

export function parseCondition(value: string): ItemCondition | null {
  const word = bare(value);
  if (!word) return "";
  return (
    ITEM_CONDITIONS.find(
      (condition) =>
        condition !== "" &&
        (condition.replace("_", " ") === word || bare(CONDITION_LABELS[condition]) === word),
    ) ?? null
  );
}

/** A whole number of units, or null when it is not one. Blank means one. */
export function parseQuantity(value: string): number | null {
  const text = value.trim();
  if (!text) return 1;
  if (!/^\d+$/.test(text)) return null;
  const count = Number(text);
  return count >= 1 && count <= MAX_QUANTITY ? count : null;
}

/** A location path as its steps: `Basement > Shelf A` or with `›`. Empty steps are dropped. */
export const locationSteps = (value: string): string[] => value.split(/[>›]/).map(clampName).filter(Boolean);

export const splitTags = (value: string): string[] => normaliseTags(value.split(/[;,|]/));

/** `YYYY-MM-DD` if it is a real calendar day, else null. Blank is null too: the caller decides what unknown means. */
export function parseDay(value: string): string | null {
  const text = value.trim();
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (!match) return null;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  const real = new Date(Date.UTC(year, month - 1, day));
  return real.getUTCFullYear() === year && real.getUTCMonth() === month - 1 && real.getUTCDate() === day
    ? text
    : null;
}

/** Money as typed, or "not a number"; blank is fine and means none. */
function money(value: string): { cents: number | null; bad: boolean } {
  if (!value.trim()) return { cents: null, bad: false };
  const cents = centsFromInput(value);
  return { cents, bad: cents === null };
}

/* ------------------------------------------------------------------ checks -- */

export interface RowCheck {
  /** Stops the row being imported until it is fixed or skipped. */
  errors: string[];
  /** Worth knowing; the row still imports. */
  warnings: string[];
}

export interface CheckContext {
  /** Whether this inventory is a stockroom, which is when cost and serials matter. */
  stock: boolean;
  /** Serials already in stock, lowercased. Only compared in a stockroom. */
  existingSerials: ReadonlySet<string>;
  /** Whether rows are being put on the shelf. A client is only attached to shelved stock. */
  shelve?: boolean;
}

const key = (serial: string): string => serial.trim().toLowerCase();

/** Every row's problems at once, so a serial repeated in the file is flagged on both rows. */
export function checkRows(sheet: ImportSheet, context: CheckContext): RowCheck[] {
  const firstSeen = new Map<string, number>();
  const counts = new Map<string, number>();
  sheet.rows.forEach((row, index) => {
    const serial = key(row.serial);
    if (!serial) return;
    counts.set(serial, (counts.get(serial) ?? 0) + 1);
    if (!firstSeen.has(serial)) firstSeen.set(serial, index);
  });

  const fieldTotal = sheet.fields.length + 1;

  return sheet.rows.map((row, index) => {
    const errors: string[] = [];
    const warnings: string[] = [];

    if (!row.title.trim() && !row.serial.trim()) errors.push("Needs a title or a serial number.");
    if (row.title.trim().length > MAX_TITLE_LENGTH)
      warnings.push(`The title will be cut to ${MAX_TITLE_LENGTH} characters.`);
    if (row.description.length > MAX_TEXT_LENGTH || row.notes.length > MAX_TEXT_LENGTH) {
      warnings.push(`Long text will be cut to ${MAX_TEXT_LENGTH} characters.`);
    }

    if (parseStatus(row.status) === null) {
      errors.push(`Status "${row.status}" is not one of keep, for sale, sold or discarded.`);
    }
    if (parseCondition(row.condition) === null) {
      errors.push(`Condition "${row.condition}" is not one of new, like new, good, fair, poor or for parts.`);
    }
    const quantity = parseQuantity(row.quantity);
    if (quantity === null)
      errors.push(`Quantity "${row.quantity}" is not a whole number from 1 to ${MAX_QUANTITY}.`);
    if (money(row.price).bad) errors.push(`Price "${row.price}" is not an amount.`);
    if (context.stock && money(row.cost).bad) errors.push(`Cost "${row.cost}" is not an amount.`);

    const serial = key(row.serial);
    if (serial) {
      if (quantity !== null && quantity > 1) {
        errors.push("A serial number is one unit, so the quantity must be 1.");
      }
      if ((counts.get(serial) ?? 0) > 1) {
        const first = firstSeen.get(serial) as number;
        errors.push(
          first === index
            ? `This serial number is repeated further down (row ${sheet.rows.findIndex((other, at) => at > index && key(other.serial) === serial) + 1}).`
            : `This serial number is already on row ${first + 1}.`,
        );
      }
      if (context.stock && context.existingSerials.has(serial)) {
        errors.push("This serial number is already in stock.");
      }
    }

    if (context.stock && context.shelve === false && row.client.trim()) {
      warnings.push(
        "The client is ignored: this row is not being put on the shelf, so there is nothing to deploy.",
      );
    }
    if (row.deployedOn.trim() && !parseDay(row.deployedOn)) {
      errors.push(`Deployed on "${row.deployedOn}" is not a date written as YYYY-MM-DD.`);
    }
    if (row.deployedOn.trim() && !row.client.trim()) {
      warnings.push("The deployed date is ignored: there is no client on this row.");
    }
    if (row.client.trim().length > MAX_NAME_LENGTH) {
      warnings.push(`The client name will be cut to ${MAX_NAME_LENGTH} characters.`);
    }

    for (const step of locationSteps(row.location)) {
      if (step.length >= MAX_NAME_LENGTH) {
        warnings.push(`A location name will be cut to ${MAX_NAME_LENGTH} characters.`);
        break;
      }
    }
    if (splitTags(row.tags).length >= MAX_TAGS_PER_ITEM)
      warnings.push(`Only the first ${MAX_TAGS_PER_ITEM} tags are kept.`);
    if (fieldTotal > MAX_FIELDS_PER_ITEM) errors.push(`An item holds at most ${MAX_FIELDS_PER_ITEM} fields.`);
    if (row.fields.some((value) => value.length > MAX_FIELD_VALUE_LENGTH)) {
      warnings.push(`A field value will be cut to ${MAX_FIELD_VALUE_LENGTH} characters.`);
    }

    return { errors, warnings };
  });
}

/* ------------------------------------------------------------------- draft -- */

/** The ids a row's names resolved to, once the screen has made whatever was missing. */
export interface ResolvedRow {
  locationId: string | null;
  categoryId: string | null;
}

/** A checked row as the item to create. Blank cells stay unset rather than overwriting defaults. */
export function buildDraft(row: ImportRow, sheet: ImportSheet, resolved: ResolvedRow): ItemDraft {
  const fields: FieldInput[] = [];
  if (row.serial.trim()) {
    fields.push({
      label: SERIAL_FIELD_LABEL,
      value: clampText(row.serial, MAX_FIELD_VALUE_LENGTH),
      visibility: "public",
    });
  }
  sheet.fields.forEach((field, index) => {
    const value = row.fields[index] ?? "";
    if (value.trim())
      fields.push({
        label: field.label,
        value: clampText(value, MAX_FIELD_VALUE_LENGTH),
        visibility: field.visibility,
      });
  });

  const draft: ItemDraft = {
    title: clampTitle(row.title || row.serial),
    description: clampText(row.description, MAX_TEXT_LENGTH),
    notes: clampText(row.notes, MAX_TEXT_LENGTH),
    locationId: resolved.locationId,
    categoryId: resolved.categoryId,
    priceCents: money(row.price).cents,
    condition: parseCondition(row.condition) ?? "",
    status: parseStatus(row.status) ?? "keep",
    quantity: parseQuantity(row.quantity) ?? 1,
    tags: splitTags(row.tags),
  };
  if (fields.length) draft.fields = fields;
  return draft;
}

/** What a stock row shelves as: one unit per serial, otherwise a counted line. */
export function shelveOf(row: ImportRow): {
  tracking: "serialized" | "bulk";
  quantity: number;
  unitCostCents: number | null;
} {
  const serialized = row.serial.trim() !== "";
  return {
    tracking: serialized ? "serialized" : "bulk",
    quantity: serialized ? 1 : (parseQuantity(row.quantity) ?? 1),
    unitCostCents: money(row.cost).cents,
  };
}

/* ---------------------------------------------------------------- template -- */

const EXAMPLES: Record<"personal" | "stock", readonly (readonly string[])[]> = {
  personal: [
    [
      "Cordless drill",
      "18V with two batteries",
      "Tools",
      "Garage > Shelf A",
      "power; dewalt",
      "1",
      "keep",
      "good",
      "45.00",
      "Bought 2021",
      "",
      "2029-03-01",
      "Hardware store",
    ],
    [
      "Camping stove",
      "",
      "Outdoors",
      "Garage > Shelf B > Bin 2",
      "camping",
      "1",
      "for sale",
      "like new",
      "30",
      "",
      "",
      "",
      "",
    ],
  ],
  stock: [
    [
      "Dell Latitude 7440",
      "14-inch laptop, 16 GB",
      "Laptops",
      "Stockroom > Rack 2 > Bin 4",
      "dell; i7",
      "1",
      "keep",
      "new",
      "",
      "",
      "5CG1234XYZ",
      "2028-06-30",
      "CDW",
      "1180.00",
      "",
    ],
    [
      "USB-C power supply 65W",
      "",
      "Power",
      "Stockroom > Shelf B",
      "",
      "20",
      "keep",
      "new",
      "",
      "",
      "",
      "",
      "CDW",
      "9.50",
      "",
    ],
  ],
};

/**
 * The header row and two example rows, in the columns the mapping screen
 * recognises on sight: upload it back and nothing needs mapping. Two custom
 * columns show the `Field:` and `Private:` prefixes. `Cost` and `Client` are only there for a
 * stockroom.
 */
export function buildTemplateCsv(stock: boolean): string {
  const columns = IMPORT_COLUMNS.filter((column) => !column.stockOnly);
  const header = [...columns.map((column) => column.label), "Field: Warranty ends", "Private: Vendor"];
  const examples = EXAMPLES[stock ? "stock" : "personal"];
  const withCost = stock ? [...header, columnSpec("cost").label, columnSpec("client").label] : header;

  // Example rows are written in the personal column order plus the two custom
  // columns, and stock rows carry cost, then client, last.
  const lines = [withCost, ...examples.map((example) => example.slice(0, withCost.length))];
  return `${lines.map((line) => line.map((cell) => csvCell(cell, false)).join(",")).join("\r\n")}\r\n`;
}
